import type { Follow } from "@tandemdiabetes/sugarmate-api-react-client"
import { Input } from "@tandemdiabetes/sugarmate-react-components"
import classNames from "classnames"
import type { ChangeEvent } from "react"
import {
  useCallback,
  useEffect,
  useState,
} from "react"

import styles from "./SleepHoursInputs.module.scss"
import { useFollowedUsers } from "src/contexts"

interface Props {
  className?: string;
  onBlur?: (sleepSettings: Follow["sleep_settings"]) => void;
  onChange?: (sleepSettings: Follow["sleep_settings"]) => void;
}

/*
 * this component allows the user to edit their own sleep_settings
 */
export function SleepHoursInputs(props: Props) {
  const {
    className,
    onBlur,
    onChange,
  } = props

  const { selfFollow } = useFollowedUsers()

  const [
    sleepState,
    setSleepState,
  ] = useState<Follow["sleep_settings"]>(selfFollow.sleep_settings)

  // when the selfFollow changes, update the sleepState
  useEffect(
    () => {
      setSleepState({
        start_time: selfFollow.sleep_settings.start_time,
        end_time: selfFollow.sleep_settings.end_time,
      })
    },
    [
      selfFollow.sleep_settings.start_time,
      selfFollow.sleep_settings.end_time,
    ],
  )

  // update the start_time
  const onStartValueChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const newSleepState = {
        ...sleepState,
        start_time: e.target.value,
      }

      setSleepState(newSleepState)
      onChange?.(newSleepState)
    },
    [
      sleepState,
      setSleepState,
      onChange,
    ],
  )

  // update the end_time
  const onEndValueChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const newSleepState = {
        ...sleepState,
        end_time: e.target.value,
      }

      setSleepState(newSleepState)
      onChange?.(newSleepState)
    },
    [
      sleepState,
      setSleepState,
      onChange,
    ],
  )

  const onValueBlur = useCallback(
    () => {
      onBlur?.(sleepState)
    },
    [
      onBlur,
      sleepState,
    ],
  )

  return (
    <div
      className={
        classNames(
          styles.sleepContainer,
          className,
        )
      }
    >
      <Input
        className={styles.sleepInput}
        type="time"
        id="sleep-time-start"
        value={sleepState.start_time}
        onChange={onStartValueChange}
        onBlur={onValueBlur}
      />
      <div className={styles.sleepInputSeparator}>
        –
      </div>
      <Input
        className={styles.sleepInput}
        type="time"
        id="sleep-time-end"
        value={sleepState.end_time}
        onChange={onEndValueChange}
        onBlur={onValueBlur}
      />
    </div>
  )
}
